import { env } from '../config/env';
import { errorReporter } from './errorReporter';

/**
 * Error reporting bootstrap — SOPHIA-35271
 *
 * Connects the ErrorReporter singleton to the backend's /api/client-errors
 * ingest endpoint (which logs reports into GCP Error Reporting) and installs
 * global handlers so uncaught errors and unhandled promise rejections are
 * reported, not just render errors caught by the ErrorBoundary.
 *
 * Reporting is fire-and-forget and must never throw: a failure to report
 * can never be allowed to break the page it is reporting on.
 */

const INGEST_URL = `${env.apiUrl}/api/client-errors`;

function sendToBackend(errorData: Record<string, unknown>): void {
  try {
    void fetch(INGEST_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // keepalive lets the report survive page unloads (e.g. crash-then-reload)
      keepalive: true,
      body: JSON.stringify({
        message: errorData.message,
        name: errorData.name,
        stack: errorData.stack,
        url: errorData.url,
        componentStack: errorData.componentStack,
        context: errorData.component ?? errorData.errorType,
      }),
    }).catch(() => {
      // Swallow: reporting failures must never surface or recurse
    });
  } catch {
    // Swallow: same reason as above
  }
}

export function initErrorReporting(): void {
  errorReporter.addService({ name: 'backend-ingest', sendError: sendToBackend });

  window.addEventListener('error', (event) => {
    errorReporter.reportError(event.error ?? new Error(event.message), undefined, {
      component: 'window.onerror',
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
    errorReporter.reportError(reason, undefined, {
      component: 'unhandledrejection',
    });
  });
}
